import { t, type StringKey } from '../strings';
import { csvCell } from '../reports/reports';
import { formatPhone } from '../records/phone';
import type { StatusColorGroup } from '../statusDefinitions';
import { CALL_STATUSES, type CallStatus } from './bridgedCall';
import { CALLS_PATH, CALLS_VOICEMAIL_PATH, type RecentCallRow } from './phoneSystem';

/**
 * 🔒 The phone system's three web pages (decision 11860; row T3708's web half): Calls, Voicemail and Texts with its
 * thread. The routes, the status pill's word and group, the row's their-number and our-number, the call's length, the
 * read's query and the Export's CSV live here, so a later phone screen (batch TW8) reads the same words.
 */

export const PHONE_CALLS_ROUTE = '/phone/calls';
export const PHONE_VOICEMAIL_ROUTE = '/phone/voicemail';
export const PHONE_TEXTS_ROUTE = '/phone/texts';
/** The thread's route as the router declares it; `on` names our number. */
export const PHONE_THREAD_ROUTE = '/phone/texts/:number';

/** The thread's address: their number in the path, ours as `on`, '' where the company number carried it unnamed. */
export function phoneThreadHref(theirNumber: string, ourNumber: string): string {
  return `${PHONE_TEXTS_ROUTE}/${encodeURIComponent(theirNumber)}?on=${encodeURIComponent(ourNumber)}`;
}

/** The status the pill reads under; the pill's word and group come from `callStatusPill`, never the seeded table. */
export const CALL_STATUS_ENTITY = 'CallStatus';

/** 🔒 Decision 11860: Missed the error group; Voicemail, No Answer and Busy the warning group; Completed the good group. */
const CALL_STATUS_GROUP: Readonly<Record<CallStatus, StatusColorGroup>> = {
  Placing: 'inactive', Ringing: 'inactive', InProgress: 'inactive', Completed: 'good',
  NoAnswer: 'warning', Busy: 'warning', Failed: 'error', Missed: 'error', Voicemail: 'warning',
};

function statusKey(status: CallStatus): StringKey {
  return `phonePages.status.${status.charAt(0).toLowerCase()}${status.slice(1)}` as StringKey;
}

/** The pill's Title Case word and its group, or null for a word that is not a call status. */
export function callStatusPill(status: string): { label: string; colorGroup: StatusColorGroup } | null {
  if (!(CALL_STATUSES as readonly string[]).includes(status)) return null;
  const known = status as CallStatus;
  return { label: t(statusKey(known)), colorGroup: CALL_STATUS_GROUP[known] };
}

/** The statuses the filter offers: the ones a finished call reads. */
export const CALL_FILTER_STATUSES: readonly CallStatus[] = ['Completed', 'Missed', 'Voicemail', 'NoAnswer', 'Busy', 'Failed'];

/** Their side of the call: the caller on an inbound call, the number called on an outbound one. */
export function theirNumber(row: Pick<RecentCallRow, 'direction' | 'fromNumber' | 'toNumber'>): string {
  return (row.direction === 'Inbound' ? row.fromNumber : row.toNumber) ?? '';
}

/** Our side of the call: the number rung on an inbound call, the caller ID on an outbound one. */
export function ourNumber(row: Pick<RecentCallRow, 'direction' | 'fromNumber' | 'toNumber'>): string {
  return (row.direction === 'Inbound' ? row.toNumber : row.fromNumber) ?? '';
}

/** A call's length as minutes and seconds, 2:05; empty for a call that never connected. */
export function callLengthText(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) return '';
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export interface CallsFilter {
  status: CallStatus | '';
  numberId: string;
  /** Decision 11900: the first and last day of the calls read, each a `YYYY-MM-DD` day or '' for no bound. */
  from: string;
  to: string;
}

/** The read's address for one page under the filter; the Voicemail list reads its own route and asks no status. */
export function callsQueryPath(list: 'calls' | 'voicemail', filter: CallsFilter, page: number, pageSize: number): string {
  const query = new URLSearchParams();
  if (list === 'calls' && filter.status !== '') query.set('status', filter.status);
  if (filter.numberId !== '') query.set('numberId', filter.numberId);
  if (filter.from !== '') query.set('from', filter.from);
  if (filter.to !== '') query.set('to', filter.to);
  query.set('page', String(page));
  query.set('pageSize', String(pageSize));
  return `${list === 'calls' ? CALLS_PATH : CALLS_VOICEMAIL_PATH}?${query.toString()}`;
}

/** 🔒 Decision 11860: Export's file, the visible columns' labels as its header and each row's drawn words beneath. */
export function callsCsv(columns: readonly { key: string; label: string }[], rows: readonly Record<string, unknown>[]): string {
  const cell = (value: unknown) => csvCell(value === null || value === undefined ? null : typeof value === 'number' ? value : String(value));
  return [columns.map((column) => cell(column.label)).join(','), ...rows.map((row) => columns.map((column) => cell(row[column.key])).join(','))].join('\r\n') + '\r\n';
}


/**
 * 🔒 Create Lead's form for a conversation with a number no Person holds (decision 11860), as the texted-card offer
 * opens it (decision 5180): source Text message, the number as the occasion and as the Lead's phone.
 */
export function textedNumberLeadValues(number: string): Record<string, unknown> {
  return { source: 'TextMessage', sourceDetail: formatPhone(number) || number, submittedName: '', submittedCompanyName: '', submittedEmail: '', submittedPhone: number };
}
